import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  flexRender,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { PiPackage, PiArrowUp, PiArrowDown } from "react-icons/pi";
import { GoPackage } from "react-icons/go";
import { FaEraser } from "react-icons/fa";
import { Button } from "react-bootstrap";

import { AuthContext } from "../context/AuthContext";
import SuccessModal from "../components/modals/SuccessModal";
import { useNotificationModal } from "../hooks/useNotificationModal";
import "../styles/StockManagement.css";
import Spinner from "../components/Spinner";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const columnHelper = createColumnHelper();

export default function StockManagement() {
  const { userData } = useContext(AuthContext);

  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showError,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  const [stockSummary, setStockSummary] = useState(null);
  const [issuedStock, setIssuedStock] = useState(null);
  const [grnStock, setGrnStock] = useState(null);
  const [mainStock, setMainStock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("stock-summary");

  // Search states
  const [stockSummarySearch, setStockSummarySearch] = useState("");
  const [issuedStockSearch, setIssuedStockSearch] = useState("");
  const [grnStockSearch, setGrnStockSearch] = useState("");
  const [mainStockSearch, setMainStockSearch] = useState("");
  // Column filter states for per-column searching
  const [stockSummaryColumnFilters, setStockSummaryColumnFilters] = useState(
    []
  );
  const [issuedStockColumnFilters, setIssuedStockColumnFilters] = useState([]);
  const [grnStockColumnFilters, setGrnStockColumnFilters] = useState([]);
  const [mainStockColumnFilters, setMainStockColumnFilters] = useState([]);

  // Clear all filters across tables
  const clearFilters = () => {
    setStockSummarySearch("");
    setIssuedStockSearch("");
    setGrnStockSearch("");
    setMainStockSearch("");

    setStockSummaryColumnFilters([]);
    setIssuedStockColumnFilters([]);
    setGrnStockColumnFilters([]);
    setMainStockColumnFilters([]);

    // refresh current tab's data
    if (activeTab === "stock-summary") fetchStockSummary();
    else if (activeTab === "main-stock") fetchMainStock();
    else if (activeTab === "issued-stock") fetchIssuedStock();
    else if (activeTab === "grn-stock") fetchGrnStock();
  };

  // Helper to convert various date filter values into yyyy-mm-dd
  const formatDateForInput = (val) => {
    if (!val) return "";
    try {
      const d = new Date(val);
      if (isNaN(d)) return "";
      return d.toISOString().slice(0, 10); // YYYY-MM-DD
    } catch (e) {
      return "";
    }
  };

  // stock summary
  const columns = [
    columnHelper.display({
      id: "index",
      header: "#",
      cell: (info) => info.row.index + 1,
    }),
    columnHelper.accessor("style_number", {
      header: "Product Number",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("style_name", {
      header: "Product Name",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("sku", {
      header: "SKU",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("stock_qty", {
      header: "Stock Quantity",
      cell: (info) => info.getValue(),
      meta: { className: "text-end" },
    }),
    columnHelper.accessor("updated_at", {
      header: "Last Updated",
      cell: (info) => new Date(info.getValue()).toLocaleDateString(),
    }),
  ];

  // issued stock
  const issuedStockColumns = [
    columnHelper.display({
      id: "index",
      header: "#",
      cell: (info) => info.row.index + 1,
    }),
    columnHelper.accessor("style_number", {
      header: "Product Number",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("style_name", {
      header: "Product Name",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("sku", {
      header: "SKU",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("batch_number", {
      header: "Batch Number",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("lot_no", {
      header: "Lot Number",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("issuing_qty", {
      header: "Issued Quantity",
      cell: (info) => info.getValue(),
      meta: { className: "text-end" },
    }),
    columnHelper.accessor("issued_at", {
      header: "Issued Date",
      cell: (info) => new Date(info.getValue()).toLocaleDateString(),
    }),
  ];

  // GRN stock
  const grnStockColumns = [
    columnHelper.display({
      id: "index",
      header: "#",
      cell: (info) => info.row.index + 1,
    }),
    columnHelper.accessor("grn_id", {
      header: "GRN ID",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("style_number", {
      header: "Product Number",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("style_name", {
      header: "Product Name",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("sku", {
      header: "SKU",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("batch_number", {
      header: "Batch Number",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("lot_no", {
      header: "Lot Number",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("stock_qty", {
      header: "Received Quantity",
      cell: (info) => info.getValue(),
      meta: { className: "text-end" },
    }),
    columnHelper.accessor("location_name", {
      header: "Location",
      cell: (info) => info.getValue() || "N/A",
    }),
    columnHelper.accessor("created_at", {
      header: "Received Date",
      cell: (info) => new Date(info.getValue()).toLocaleDateString(),
    }),
  ];

  // main stock
  const mainStockColumns = [
    columnHelper.display({
      id: "index",
      header: "#",
      cell: (info) => info.row.index + 1,
    }),
    columnHelper.accessor("style_number", {
      header: "Product Number",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("style_name", {
      header: "Product Name",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("sku", {
      header: "SKU",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("batch_number", {
      header: "Batch Number",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("lot_no", {
      header: "Lot Number",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("unit_price", {
      header: "Unit Price",
      cell: (info) => info.getValue(),
      meta: { className: "number-col" },
    }),
    columnHelper.accessor("sale_price", {
      header: "Sale Price",
      cell: (info) => info.getValue(),
      meta: { className: "number-col" },
    }),
    columnHelper.accessor("main_stock_qty", {
      header: "Quantity",
      cell: (info) => info.getValue(),
      meta: { className: "number-col" },
    }),
    columnHelper.accessor("location_name", {
      header: "Location",
      cell: (info) => info.getValue() || "N/A",
    }),
    columnHelper.accessor("created_at", {
      header: "Added Date",
      cell: (info) => new Date(info.getValue()).toLocaleDateString(),
    }),
  ];

  const table = useReactTable({
    data: stockSummary || [],
    columns,
    // enable per-column filtering by default using includesString
    defaultColumn: { filterFn: "includesString" },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "includesString",
    state: {
      globalFilter: stockSummarySearch,
      columnFilters: stockSummaryColumnFilters,
    },
    onGlobalFilterChange: setStockSummarySearch,
    onColumnFiltersChange: setStockSummaryColumnFilters,
  });

  const issuedStockTable = useReactTable({
    data: issuedStock || [],
    columns: issuedStockColumns,
    defaultColumn: { filterFn: "includesString" },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "includesString",
    state: {
      globalFilter: issuedStockSearch,
      columnFilters: issuedStockColumnFilters,
    },
    onGlobalFilterChange: setIssuedStockSearch,
    onColumnFiltersChange: setIssuedStockColumnFilters,
  });

  const grnStockTable = useReactTable({
    data: grnStock || [],
    columns: grnStockColumns,
    defaultColumn: { filterFn: "includesString" },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "includesString",
    state: {
      globalFilter: grnStockSearch,
      columnFilters: grnStockColumnFilters,
    },
    onGlobalFilterChange: setGrnStockSearch,
    onColumnFiltersChange: setGrnStockColumnFilters,
  });

  const mainStockTable = useReactTable({
    data: mainStock || [],
    columns: mainStockColumns,
    defaultColumn: { filterFn: "includesString" },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "includesString",
    state: {
      globalFilter: mainStockSearch,
      columnFilters: mainStockColumnFilters,
    },
    onGlobalFilterChange: setMainStockSearch,
    onColumnFiltersChange: setMainStockColumnFilters,
  });

  // fetch stock summary
  const fetchStockSummary = async (page = 1, search = "") => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${BASE_URL}/api/admin/stock/main-stock-summary`,
        {
          params: {
            company_code: userData?.company_code,
            page: page,
            limit: 15,
            type: "main",
            style_number: search || undefined,
            style_name: search || undefined,
          },
        }
      );
      setStockSummary(response.data.data);
    } catch (err) {
      setError("Failed to fetch stock summary");
      showError("Failed to fetch stock summary", "Error");
      console.error("Error fetching stock summary:", err);
    } finally {
      setLoading(false);
    }
  };

  // fetch issued stock
  const fetchIssuedStock = async (page = 1, search = "") => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${BASE_URL}/api/admin/stock/issued-stock`,
        {
          params: {
            company_code: userData?.company_code,
            page: page,
            limit: 15,
            type: "issued",
            style_number: search || undefined,
            style_name: search || undefined,
          },
        }
      );
      setIssuedStock(response.data.data);
    } catch (err) {
      setError("Failed to fetch issued stock");
      showError("Failed to fetch issued stock", "Error");
      console.error("Error fetching issued stock:", err);
    } finally {
      setLoading(false);
    }
  };

  // fetch GRN stock
  const fetchGrnStock = async (page = 1, search = "") => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${BASE_URL}/api/admin/stock/grn-stock`,
        {
          params: {
            company_code: userData?.company_code,
            page: page,
            limit: 15,
            type: "grn",
            style_number: search || undefined,
            style_name: search || undefined,
          },
        }
      );
      setGrnStock(response.data.data);
    } catch (err) {
      setError("Failed to fetch GRN stock");
      showError("Failed to fetch GRN stock", "Error");
      console.error("Error fetching GRN stock:", err);
    } finally {
      setLoading(false);
    }
  };

  // fetch main stock data
  const fetchMainStock = async (page = 1, search = "") => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${BASE_URL}/api/admin/stock/main-stock`,
        {
          params: {
            company_code: userData?.company_code,
            page: page,
            limit: 15,
            style_number: search || undefined,
            style_name: search || undefined,
          },
        }
      );
      setMainStock(response.data.data);
    } catch (err) {
      setError("Failed to fetch main stock");
      showError("Failed to fetch main stock", "Error");
      console.error("Error fetching main stock:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change and fetch appropriate data
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (tabId === "stock-summary") {
      fetchStockSummary(1, stockSummarySearch);
    } else if (tabId === "main-stock") {
      fetchMainStock(1, mainStockSearch);
    } else if (tabId === "issued-stock") {
      fetchIssuedStock(1, issuedStockSearch);
    } else if (tabId === "grn-stock") {
      fetchGrnStock(1, grnStockSearch);
    }
  };

  // Fetch initial data for each tab
  useEffect(() => {
    if (!userData?.company_code) return;
    fetchStockSummary();
    fetchIssuedStock();
    fetchGrnStock();
    fetchMainStock();
  }, [userData?.company_code]);

  if (loading) return <Spinner />;
  if (error) return <div>Error: {error}</div>;

  const renderTabContent = () => {
    switch (activeTab) {
      case "stock-summary":
        return (
          <div className="stock-summary-table">
            <div className="table-header">
              <h2>Total Stock Summary</h2>
            </div>
            {stockSummary && (
              <>
                <div
                  className="table-scroll-wrapper"
                  role="region"
                  aria-label="Stock Summary Table"
                >
                  <table className="stock-summary-table">
                    <thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <React.Fragment key={headerGroup.id}>
                          <tr>
                            {headerGroup.headers.map((header) => (
                              <th key={header.id}>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </th>
                            ))}
                          </tr>
                          <tr className="column-filters-row">
                            {headerGroup.headers.map((header) => {
                              // render clear filters button in the index column's filter cell
                              const colId = header.column?.id || header.id;
                              if (colId === "index") {
                                return (
                                  <th key={header.id}>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Button
                                        variant="outline"
                                        className="pf-clear-filters-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          clearFilters();
                                        }}
                                        title="Clear filters"
                                      >
                                        <FaEraser />
                                      </Button>
                                    </div>
                                  </th>
                                );
                              }

                              const headerLabel = String(
                                header.column.columnDef.header || ""
                              ).toLowerCase();
                              const isDateCol = /date|updated|created/.test(
                                headerLabel
                              );
                              return (
                                <th key={header.id}>
                                  {header.column.getCanFilter() ? (
                                    isDateCol ? (
                                      <input
                                        type="date"
                                        value={formatDateForInput(
                                          header.column.getFilterValue()
                                        )}
                                        onChange={(e) =>
                                          header.column.setFilterValue(
                                            e.target.value || undefined
                                          )
                                        }
                                        className="sst-search-input"
                                      />
                                    ) : (
                                      <input
                                        type="text"
                                        value={
                                          header.column.getFilterValue() ?? ""
                                        }
                                        onChange={(e) =>
                                          header.column.setFilterValue(
                                            e.target.value
                                          )
                                        }
                                        placeholder={`Search ${String(
                                          header.column.columnDef.header
                                        )}`}
                                        className="sst-search-input"
                                      />
                                    )
                                  ) : null}
                                </th>
                              );
                            })}
                          </tr>
                        </React.Fragment>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell) => {
                            const className =
                              cell.column.columnDef.meta?.className || "";
                            return (
                              <td key={cell.id} className={className}>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        );
      case "issued-stock":
        return (
          <div className="issued-stock-table">
            <div className="table-header">
              <h2>Issued Stock</h2>
            </div>
            {issuedStock && (
              <>
                <div
                  className="table-scroll-wrapper"
                  role="region"
                  aria-label="Issued Stock Table"
                >
                  <table>
                    <thead>
                      {issuedStockTable.getHeaderGroups().map((headerGroup) => (
                        <React.Fragment key={headerGroup.id}>
                          <tr>
                            {headerGroup.headers.map((header) => (
                              <th key={header.id}>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </th>
                            ))}
                          </tr>
                          <tr className="column-filters-row">
                            {headerGroup.headers.map((header) => {
                              const colId = header.column?.id || header.id;
                              if (colId === "index") {
                                return (
                                  <th key={header.id}>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Button
                                        variant="outline-danger"
                                        className="pf-clear-filters-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          clearFilters();
                                        }}
                                        title="Clear filters"
                                      >
                                        <FaEraser />
                                      </Button>
                                    </div>
                                  </th>
                                );
                              }

                              const headerLabel = String(
                                header.column.columnDef.header || ""
                              ).toLowerCase();
                              const isDateCol = /date|updated|created/.test(
                                headerLabel
                              );
                              return (
                                <th key={header.id}>
                                  {header.column.getCanFilter() ? (
                                    isDateCol ? (
                                      <input
                                        type="date"
                                        value={formatDateForInput(
                                          header.column.getFilterValue()
                                        )}
                                        onChange={(e) =>
                                          header.column.setFilterValue(
                                            e.target.value || undefined
                                          )
                                        }
                                        className="ist-search-input"
                                      />
                                    ) : (
                                      <input
                                        type="text"
                                        value={
                                          header.column.getFilterValue() ?? ""
                                        }
                                        onChange={(e) =>
                                          header.column.setFilterValue(
                                            e.target.value
                                          )
                                        }
                                        placeholder={`Search ${String(
                                          header.column.columnDef.header
                                        )}`}
                                        className="ist-search-input"
                                      />
                                    )
                                  ) : null}
                                </th>
                              );
                            })}
                          </tr>
                        </React.Fragment>
                      ))}
                    </thead>
                    <tbody>
                      {issuedStockTable.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell) => {
                            const className =
                              cell.column.columnDef.meta?.className || "";
                            return (
                              <td key={cell.id} className={className}>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        );
      case "main-stock":
        return (
          <div className="main-stock-table">
            <div className="table-header">
              <h2>Main Stock</h2>
            </div>
            {mainStock && (
              <>
                <div
                  className="table-scroll-wrapper"
                  role="region"
                  aria-label="Main Stock Table"
                >
                  <table>
                    <thead>
                      {mainStockTable.getHeaderGroups().map((headerGroup) => (
                        <React.Fragment key={headerGroup.id}>
                          <tr>
                            {headerGroup.headers.map((header) => (
                              <th key={header.id}>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </th>
                            ))}
                          </tr>
                          <tr className="column-filters-row">
                            {headerGroup.headers.map((header) => {
                              const colId = header.column?.id || header.id;
                              if (colId === "index") {
                                return (
                                  <th key={header.id}>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Button
                                        variant="outline-danger"
                                        className="pf-clear-filters-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          clearFilters();
                                        }}
                                        title="Clear filters"
                                      >
                                        <FaEraser />
                                      </Button>
                                    </div>
                                  </th>
                                );
                              }

                              const headerLabel = String(
                                header.column.columnDef.header || ""
                              ).toLowerCase();
                              const isDateCol = /date|updated|created/.test(
                                headerLabel
                              );
                              return (
                                <th key={header.id}>
                                  {header.column.getCanFilter() ? (
                                    isDateCol ? (
                                      <input
                                        type="date"
                                        value={formatDateForInput(
                                          header.column.getFilterValue()
                                        )}
                                        onChange={(e) =>
                                          header.column.setFilterValue(
                                            e.target.value || undefined
                                          )
                                        }
                                        className="mst-search-input"
                                      />
                                    ) : (
                                      <input
                                        type="text"
                                        value={
                                          header.column.getFilterValue() ?? ""
                                        }
                                        onChange={(e) =>
                                          header.column.setFilterValue(
                                            e.target.value
                                          )
                                        }
                                        placeholder={`Search ${String(
                                          header.column.columnDef.header
                                        )}`}
                                        className="mst-search-input"
                                      />
                                    )
                                  ) : null}
                                </th>
                              );
                            })}
                          </tr>
                        </React.Fragment>
                      ))}
                    </thead>
                    <tbody>
                      {mainStockTable.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell) => {
                            const className =
                              cell.column.columnDef.meta?.className || "";
                            return (
                              <td key={cell.id} className={className}>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        );
      case "grn-stock":
        return (
          <div className="grn-stock-table">
            <div className="table-header">
              <h2>GRN Stock</h2>
            </div>
            {grnStock && (
              <>
                <div
                  className="table-scroll-wrapper"
                  role="region"
                  aria-label="GRN Stock Table"
                >
                  <table>
                    <thead>
                      {grnStockTable.getHeaderGroups().map((headerGroup) => (
                        <React.Fragment key={headerGroup.id}>
                          <tr>
                            {headerGroup.headers.map((header) => (
                              <th key={header.id}>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </th>
                            ))}
                          </tr>
                          <tr className="column-filters-row">
                            {headerGroup.headers.map((header) => {
                              const colId = header.column?.id || header.id;
                              if (colId === "index") {
                                return (
                                  <th key={header.id}>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Button
                                        variant="outline"
                                        className="pf-clear-filters-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          clearFilters();
                                        }}
                                        title="Clear filters"
                                      >
                                        <FaEraser />
                                      </Button>
                                    </div>
                                  </th>
                                );
                              }

                              const headerLabel = String(
                                header.column.columnDef.header || ""
                              ).toLowerCase();
                              const isDateCol = /date|updated|created/.test(
                                headerLabel
                              );
                              return (
                                <th key={header.id}>
                                  {header.column.getCanFilter() ? (
                                    isDateCol ? (
                                      <input
                                        type="date"
                                        value={formatDateForInput(
                                          header.column.getFilterValue()
                                        )}
                                        onChange={(e) =>
                                          header.column.setFilterValue(
                                            e.target.value || undefined
                                          )
                                        }
                                        className="gst-search-input"
                                      />
                                    ) : (
                                      <input
                                        type="text"
                                        value={
                                          header.column.getFilterValue() ?? ""
                                        }
                                        onChange={(e) =>
                                          header.column.setFilterValue(
                                            e.target.value
                                          )
                                        }
                                        placeholder={`Search ${String(
                                          header.column.columnDef.header
                                        )}`}
                                        className="gst-search-input"
                                      />
                                    )
                                  ) : null}
                                </th>
                              );
                            })}
                          </tr>
                        </React.Fragment>
                      ))}
                    </thead>
                    <tbody>
                      {grnStockTable.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell) => {
                            const className =
                              cell.column.columnDef.meta?.className || "";
                            return (
                              <td key={cell.id} className={className}>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const tabItems = [
    { id: "stock-summary", label: "Total Stock Summary", icon: PiPackage },
    { id: "main-stock", label: "Main Stock", icon: GoPackage },
    { id: "grn-stock", label: "GRN Stock", icon: PiArrowDown },
    { id: "issued-stock", label: "Issued Stock", icon: PiArrowUp },
  ];

  return (
    <div className="stock-management">
      {/* Navigation Tabs */}
      <div className="nav-tabs-custom">
        <ul className="nav nav-tabs border-0">
          {tabItems.map((item) => (
            <li className="nav-item" key={item.id}>
              <button
                className={`nav-link d-flex tab-title ${
                  activeTab === item.id ? "active" : ""
                }`}
                onClick={() => handleTabChange(item.id)}
              >
                {item.icon && <item.icon size={18} className="me-2" />}
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Content */}
      <div className="tab-content">
        <div className="tab-pane active">{renderTabContent()}</div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        title={successTitle}
        message={successMessage}
        variant="success"
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        title={errorTitle}
        message={errorMessage}
        variant="error"
      />
    </div>
  );
}
